// Core
import { useState } from 'react';
// Packages
import { useLocation, useHistory, Link } from 'react-router-dom';
import cn from 'classnames';
// Components
import { paginationSettings } from 'components/Table/paginationSettings';
import { Menu } from 'components/lib';
import { TMenuInfo, TMenuItem } from 'components/lib/Menu';
// Routes
import routes, { ROUTES, TRoute } from 'router/routes';
// Custom Hooks
import { useCompanyPath, useRoutePermissions, useBreakpoint } from 'customHooks';
// Redux
import { useAppSelector, useAppDispatch, STORE_KEYS } from 'redux/store';
import { toggleModal, IPageName } from 'redux/nav/navReducer';
// Helpers
import { setQueryString } from 'helpers/queryString';
import {
  getSubMenuSecondLevelItemName,
  getSubMenuThirdLevelItemName,
  ILabelSecondLevelMenu,
  ILabelThirdLevelMenu,
} from './getMenuItemName';
import getAccessToPage from './getAccessToPage';
import { CREATE_ACTION_ICON, getMenuIcon, getSubMenuIcon } from './menuIcons';
// Types
import { TPermissionKey } from 'customHooks/usePermissions';
// I18n
import { t } from 'i18n';
// Styles
import styles from './navigation.module.scss';

interface IProps {
  isMenuVisible?: boolean;
  mode?: 'horizontal' | 'inline' | 'vertical';
  isCollapsed?: boolean;
  openKeys?: string[];
  subMenuOpenDelay?: number;
  onToggleMenu?: () => void;
}

interface IFirstLevelMenuItemProps {
  path: string;
  defaultQueryStringOnPage: string;
  type?: 'list' | 'default';
}

interface ISecondLevelMenuItemProps extends IFirstLevelMenuItemProps {
  label: string;
  permissionKey: string;
}

interface IModal {
  pageName: IPageName;
  shouldModalBeOpen: boolean;
}

const getEqualPath = (path: string) => {
  return path.split('/').slice(2).join('/');
};

export const getAllowedPagesRoutesWithoutHelpPage = (allowedR: TRoute[]) => {
  return allowedR.filter(({ isNavigate, label }) => isNavigate && label !== 'helpPage');
};

const Navigation = ({
  isMenuVisible,
  mode = 'horizontal',
  isCollapsed,
  openKeys,
  subMenuOpenDelay,
  onToggleMenu,
}: IProps) => {
  const { pathname } = useLocation();
  const history = useHistory();

  const { lg } = useBreakpoint();

  const { getUpdatedPath } = useCompanyPath();
  const allowedRoutes = useRoutePermissions(routes);

  const { user } = useAppSelector(STORE_KEYS.USER);

  const dispatch = useAppDispatch();

  const [currentKey, setCurrentKey] = useState<string>(pathname);

  const handleClick = ({ key }: TMenuInfo) => {
    setCurrentKey(key as string);

    onToggleMenu && onToggleMenu();
  };

  const selectedKey = getUpdatedPath(pathname).split('/').slice(0, 3).join('/');
  // Same rule as the header's recent pages (NavigationMemory): the section the URL is in.
  const pathWithoutCompanyName = selectedKey.split('/')[2];
  // The whole section segment: a substring match lit up every item whose path contains it
  const isCurrentSection = (path: string) =>
    !!pathWithoutCompanyName && path.split('/')[2] === pathWithoutCompanyName;
  const getNavMenuItemClass = (path: string) =>
    cn(styles.navMenuItem, { [styles.navMenuItemActive]: isCurrentSection(path) });

  const { current: page } = paginationSettings;
  const defaultQueryString = setQueryString({ page, limit: user?.paginationPreference });
  const defaultQueryStringWithActiveFilter = setQueryString({
    page,
    limit: user?.paginationPreference,
    status: 'active',
  });

  if (getEqualPath(ROUTES.PAYMENT_CONFIRMED) === getEqualPath(pathname)) {
    return null;
  }

  const handleGoToMainMenuPage = ({
    path,
    type,
    defaultQueryStringOnPage,
  }: IFirstLevelMenuItemProps) => {
    history.push(
      `${getUpdatedPath(path)}${type === 'list' ? `?${defaultQueryStringOnPage}` : ''}`,
    );

    isMenuVisible && onToggleMenu && onToggleMenu();
  };

  const handleGoToHelpPage = () => {
    window.location.replace('https://help.timego.com');
  };

  const handleOpenCertainPageModal = ({ pageName, shouldModalBeOpen }: IModal) => {
    dispatch(toggleModal({ pageName, shouldModalBeOpen }));
  };

  const getSecondLevelMenuItem = ({
    path,
    defaultQueryStringOnPage,
    type,
    label,
    permissionKey,
  }: ISecondLevelMenuItemProps): TMenuItem => {
    if (!permissionKey || !user) {
      return null;
    }

    const accessToPage = getAccessToPage({
      platformPermissions: user?.platformPermissions,
      neededPermissions: permissionKey as TPermissionKey,
    });

    return getSubMenuSecondLevelItemName(label as any) && accessToPage?.create
      ? {
          key: `mainMenu${getUpdatedPath(path)}${label}`,
          icon: CREATE_ACTION_ICON,
          onClick: () =>
            handleOpenCertainPageModal({
              pageName: label as IPageName,
              shouldModalBeOpen: true,
            }),
          label: (
            <Link
              to={{
                pathname: `${getUpdatedPath(path)}`,
                state: { fromMenu: true },
                search: type === 'list' ? `?${defaultQueryStringOnPage}` : '?',
              }}
            >
              {getSubMenuSecondLevelItemName(label as ILabelSecondLevelMenu)}
            </Link>
          ),
        }
      : null;
  };

  const getThirdLevelMenuItem = (
    subRoutes: TRoute['routes'][number],
    i: number,
    defaultQueryStringOnPage: string,
    canCreate?: boolean,
  ): TMenuItem =>
    !getSubMenuThirdLevelItemName(subRoutes.label as ILabelThirdLevelMenu) || !canCreate
      ? null
      : {
          key: `subMenu${getUpdatedPath(subRoutes.path)}${i}`,
          icon: CREATE_ACTION_ICON,
          onClick: () =>
            handleOpenCertainPageModal({
              pageName: subRoutes.label as IPageName,
              shouldModalBeOpen: true,
            }),
          label: (
            <Link
              to={{
                pathname: `${getUpdatedPath(subRoutes.path)}`,
                search: subRoutes.type === 'list' ? `?${defaultQueryStringOnPage}` : '?',
              }}
            >
              {getSubMenuThirdLevelItemName(subRoutes.label as ILabelThirdLevelMenu)}
            </Link>
          ),
        };

  const desktopNav: TMenuItem[] = [
    ...getAllowedPagesRoutesWithoutHelpPage(allowedRoutes),
  ]?.map(({ path, label, type, routes, permissionKey }): TMenuItem => {
    const isActiveFilterShouldBeAdded = label === 'employeesPage';
    const defaultQueryStringOnPage = isActiveFilterShouldBeAdded
      ? defaultQueryStringWithActiveFilter
      : defaultQueryString;

    if (label !== 'helpPage') {
      return {
        key: `mainMenu${getUpdatedPath(path)}${label}${type}`,
        icon: getMenuIcon(label),
        label: t(`mainNav.${label}`),
        onTitleClick: () => handleGoToMainMenuPage({ path, type, defaultQueryStringOnPage }),
        className: getNavMenuItemClass(path),
        'data-cy': `navSection-${label}`,
        // the section the URL is in, for a test that must not read the `navMenuItemActive` class
        'data-active': isCurrentSection(path),
        children:
          routes.length === 0
            ? [
                getSecondLevelMenuItem({
                  defaultQueryStringOnPage,
                  path,
                  type,
                  label,
                  permissionKey,
                }),
              ]
            : routes.map((subRoutes, i): TMenuItem => {
                const isActiveFilterShouldBeAdded =
                  subRoutes.label === 'employeeList' || subRoutes.label === 'invoiceAging';
                const defaultQueryStringOnPage = isActiveFilterShouldBeAdded
                  ? defaultQueryStringWithActiveFilter
                  : defaultQueryString;

                const accessToPage = getAccessToPage({
                  platformPermissions: user?.platformPermissions,
                  neededPermissions: subRoutes.permissionKey as TPermissionKey,
                });

                // Special case: If Manager has Full Access to checkpoints, allow access to checkpointSettings
                if (subRoutes?.path === ROUTES.CHECKPOINTS_SETTINGS && !accessToPage?.read) {
                  const checkpointsAccess = getAccessToPage({
                    platformPermissions: user?.platformPermissions,
                    neededPermissions: 'checkpoints' as TPermissionKey,
                  });
                  const hasFullCheckpointsAccess =
                    checkpointsAccess?.read &&
                    checkpointsAccess?.create &&
                    checkpointsAccess?.edit &&
                    checkpointsAccess?.delete;
                  if (!hasFullCheckpointsAccess) {
                    return null;
                  }
                } else if (!accessToPage?.read) {
                  return null;
                }

                return {
                  key: `subMenu${getUpdatedPath(subRoutes.path)}${subRoutes.label}`,
                  icon: getSubMenuIcon(subRoutes.label),
                  label: t(`subNav.${subRoutes.label}`),
                  onTitleClick: () =>
                    handleGoToMainMenuPage({
                      path: subRoutes.path,
                      type: subRoutes.type,
                      defaultQueryStringOnPage: defaultQueryStringOnPage,
                    }),
                  className: styles.navMenuItemSubMenu,
                  'data-cy': `navItem-${subRoutes.label}`,
                  children: [
                    getThirdLevelMenuItem(
                      subRoutes,
                      i,
                      defaultQueryStringOnPage,
                      accessToPage?.create,
                    ),
                  ],
                };
              }),
      };
    }

    return {
      key: getUpdatedPath(path),
      icon: getMenuIcon(label),
      label: t(`mainNav.${label}`),
      onTitleClick: handleGoToHelpPage,
      className: styles.navMenuItem,
      children: [],
    };
  });

  const mobileNav: TMenuItem[] = [...getAllowedPagesRoutesWithoutHelpPage(allowedRoutes)]?.map(
    ({ path, label, type }): TMenuItem => {
      const isActiveFilterShouldBeAdded = label === 'employeesPage';
      const defaultQueryStringOnPage = isActiveFilterShouldBeAdded
        ? defaultQueryStringWithActiveFilter
        : defaultQueryString;

      if (label !== 'helpPage') {
        return {
          key: getUpdatedPath(path),
          icon: getMenuIcon(label),
          className: getNavMenuItemClass(path),
          label: (
            <Link
              to={`${getUpdatedPath(path)}${
                type === 'list' ? `?${defaultQueryStringOnPage}` : ''
              }`}
              aria-current={isCurrentSection(path) ? 'page' : undefined}
            >
              {t(`mainNav.${label}`)}
            </Link>
          ),
        };
      }

      return {
        key: getUpdatedPath(path),
        icon: getMenuIcon(label),
        className: styles.navMenuItem,
        label: <a href="https://help.timego.com"> {t(`mainNav.${label}`)}</a>,
      };
    },
  );

  const nav = lg ? desktopNav : mobileNav;

  return (
    <>
      {isMenuVisible && (
        <Menu
          theme="light"
          mode={mode}
          inlineCollapsed={isCollapsed}
          openKeys={openKeys}
          subMenuOpenDelay={subMenuOpenDelay}
          selectedKeys={[selectedKey]}
          defaultSelectedKeys={[currentKey]}
          onClick={handleClick}
          className={styles.navMenu}
          items={nav}
        />
      )}
    </>
  );
};

export default Navigation;
